import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetMenuQuery, filterMenuByPeriod, filterMenuByMode, filterMenuByServiceMode } from '@/app/api/resourceApi';
import { formatPrice } from '@/components/ui/Price';
import MaskIcon from '@/components/ui/MaskIcon';
import DishCodeTag from '@/features/menu/DishCodeTag';
import { resolveModePrice, modeToPriceType, type ConsumerMode } from '@/features/menu/resolveModePrice';
import { computeUnitPrice } from '@/features/menu/computeUnitPrice';
import { fromPriceCents } from '@/features/menu/fromPrice';
import { sortMenuItems } from '@/features/menu/sortMenuItems';
import { categoryColor, onCategoryColor } from '@/features/menu/categoryColor';
import { thumbPhoto } from '@/features/menu/photoVariant';
import { isEffectivelyRequired } from '@/features/menu/optionRequirement';
import { buffetAddBlockMessage } from '@/features/menu/buffetAddGate';
import type { Period } from '@/features/menu/servicePeriod';
import {
  selectWaiterCartLines,
  selectWaiterCartQty,
  selectWaiterCartTotal,
  addWaiterLine,
  setWaiterLineQty,
} from '@/features/waiter/waiterCartSlice';
import type { MenuItem, OptionGroup, CartLine, SelectedOption } from '@/types';

export interface TicketPlatsToastAction {
  label: string;
  onClick: () => void;
}

export interface TicketPlatsProps {
  hidden: boolean;
  restaurantUuid: string;
  sessionUuid: string;
  /** Ticket kind — drives price mode (`sur_place`/`emporter`) and menu
   *  availability filtering (`available_sur_place`/`available_emporter`). */
  kind: 'dine_in' | 'takeaway';
  /** From `currentPeriod()`, computed by `TicketPage` — no scheduling exists
   *  on the waiter surface, so both kinds filter by "now". */
  period: Period;
  isBuffetDineIn: boolean;
  /** Best-effort — absent until the backend session serializer carries it. */
  buffetRoundItemBudget?: number | null;
  /** D5 — a NEW optioned line whose options modal must open immediately
   *  (some group is required). F3 owns the modal; this only signals intent. */
  onOpenOptionsForNewLine: (itemId: string) => void;
  /** Round blue button on a row — note/options of the LAST plate added of
   *  that product (old-app rule, SC-842). */
  onEditLastLine: (itemId: string) => void;
  /** 🛒 — opens the cart sheet (F3) and the toast action on a blocked `−`. */
  onOpenCart: () => void;
  /** Plats footer "Valider" — opens the confirm-round flow (F4). */
  onValidate: () => void;
  showToast: (message: string, action?: TicketPlatsToastAction) => void;
}

function buildPlainLine(
  item: MenuItem,
  groups: OptionGroup[],
  selected: SelectedOption[],
  isBuffetDineIn: boolean,
  mode: ConsumerMode,
): CartLine {
  return {
    lineId: uuid(),
    itemId: item.id,
    qty: 1,
    selectedOptions: selected,
    unitPrice: computeUnitPrice(item, groups, selected, isBuffetDineIn, mode),
    snapshot: { name: item.name, photo: item.photo, code: item.code },
  };
}

/**
 * 04-ux-mobile-spec.md §2.1/§2.2, 06-test-cases.md W-T-07..30 — the Plats tab:
 * category rail (1/3) + product rows (2 lines, photo background, stepper) +
 * the Plats footer (cart / total / Valider). Options/cart modals are F3's;
 * the confirm-round flow is F4's — both are reached only through the
 * callback props above.
 */
export default function TicketPlats({
  hidden,
  restaurantUuid,
  sessionUuid,
  kind,
  period,
  isBuffetDineIn,
  buffetRoundItemBudget,
  onOpenOptionsForNewLine,
  onEditLastLine,
  onOpenCart,
  onValidate,
  showToast,
}: TicketPlatsProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const consumerMode: ConsumerMode = kind === 'takeaway' ? 'takeaway' : 'dine_in';
  const priceMode = modeToPriceType(consumerMode);

  const { data: menu } = useGetMenuQuery(restaurantUuid, { skip: !restaurantUuid });

  const visibleMenu = useMemo(() => {
    if (!menu) return menu;
    return filterMenuByServiceMode(
      filterMenuByMode(filterMenuByPeriod(menu, period), priceMode),
      isBuffetDineIn ? 'buffet' : 'classic',
    );
  }, [menu, period, priceMode, isBuffetDineIn]);

  const sortedCats = useMemo(
    () => [...(visibleMenu?.categories ?? [])].sort((a, b) => a.order - b.order),
    [visibleMenu],
  );
  const itemsByCat = useMemo(() => {
    const byCat = new Map<string, MenuItem[]>();
    for (const cat of sortedCats) {
      byCat.set(
        cat.id,
        sortMenuItems(
          (visibleMenu?.items ?? []).filter((i) => i.categoryId === cat.id),
          cat.useCustomProductOrder ?? false,
        ),
      );
    }
    return byCat;
  }, [sortedCats, visibleMenu]);
  // A category whose only products got filtered out (period/mode/service
  // mode) renders no tile — tapping it would show nothing (W-T-09).
  const pickableCats = useMemo(
    () => sortedCats.filter((c) => (itemsByCat.get(c.id)?.length ?? 0) > 0),
    [sortedCats, itemsByCat],
  );

  const [selectedCatId, setSelectedCatId] = useState<string | null>(null);
  useEffect(() => {
    if (selectedCatId && pickableCats.some((c) => c.id === selectedCatId)) return;
    setSelectedCatId(pickableCats[0]?.id ?? null);
  }, [pickableCats, selectedCatId]);

  const items = selectedCatId ? (itemsByCat.get(selectedCatId) ?? []) : [];

  // §4 — a Map<categoryId, scrollTop>, restored with a direct `.scrollTop =`
  // assignment (never `scrollTo`/`scrollIntoView`, which would inherit
  // `html { scroll-behavior: smooth }` from site.css — W-T-10).
  const productListRef = useRef<HTMLDivElement>(null);
  const scrollPositions = useRef(new Map<string, number>());
  const previousCatId = useRef<string | null>(null);
  useEffect(() => {
    const container = productListRef.current;
    if (!container) return;
    if (previousCatId.current && previousCatId.current !== selectedCatId) {
      scrollPositions.current.set(previousCatId.current, container.scrollTop);
    }
    container.scrollTop = selectedCatId ? (scrollPositions.current.get(selectedCatId) ?? 0) : 0;
    previousCatId.current = selectedCatId;
  }, [selectedCatId]);

  const cartLines = useAppSelector(selectWaiterCartLines(sessionUuid));
  // Total units, not distinct lines: the badge and the roll below both read
  // it, so five of the same dish show `5` (SC-854).
  const cartQty = useAppSelector(selectWaiterCartQty(sessionUuid));
  const totalCents = useAppSelector(selectWaiterCartTotal(sessionUuid));

  // The 🛒 rolls once per ADD. Decrements never animate — pulling a dish
  // back is not a success.
  const [cartBump, setCartBump] = useState(0);
  const previousCartQty = useRef(cartQty);
  useEffect(() => {
    if (cartQty > previousCartQty.current) setCartBump((n) => n + 1);
    previousCartQty.current = cartQty;
  }, [cartQty]);

  const linesByItem = useMemo(() => {
    const map = new Map<string, CartLine[]>();
    for (const line of cartLines) {
      const arr = map.get(line.itemId) ?? [];
      arr.push(line);
      map.set(line.itemId, arr);
    }
    return map;
  }, [cartLines]);

  // buffetAddGate — advisory only, mirrors MenuPage's own gate (server stays
  // authoritative). maxRounds/cooldown aren't yet on the waiter session
  // detail contract, so only the per-round item budget is enforced here.
  function isBlockedByBuffetGate(): boolean {
    const roundItemCount = cartLines.reduce((s, l) => s + l.qty, 0);
    const blockMessage = buffetAddBlockMessage({
      isBuffetDineIn,
      roundItemCount,
      roundItemBudget: buffetRoundItemBudget ?? null,
      roundCount: 0,
      maxRounds: null,
      cooldownSecondsRemaining: 0,
      t,
    });
    if (blockMessage) {
      showToast(blockMessage);
      return true;
    }
    return false;
  }

  function handlePlus(item: MenuItem, groups: OptionGroup[], hasOptions: boolean) {
    if (isBlockedByBuffetGate()) return;
    if (hasOptions && groups.some((g) => isEffectivelyRequired(g))) {
      onOpenOptionsForNewLine(item.id);
      return;
    }
    // Plain product, or an optioned product whose groups are all optional
    // (W-T-22) — added directly; `addWaiterLine` merges into the matching
    // line (same options/comment) rather than creating a duplicate.
    dispatch(
      addWaiterLine({
        sessionUuid,
        line: buildPlainLine(item, groups, [], isBuffetDineIn, consumerMode),
      }),
    );
  }

  function handleMinus(item: MenuItem, hasOptions: boolean) {
    const itemLines = linesByItem.get(item.id) ?? [];
    if (itemLines.length === 0) return;
    const plainLine = itemLines.find((l) => l.selectedOptions.length === 0 && !l.note);
    const hasOptionedLine = itemLines.some((l) => l.selectedOptions.length > 0 || !!l.note);
    // Old-app rule: « − » on the row only ever pulls a PLAIN unit (no
    // options, no comment). Once a unit carries options or a note, WHICH
    // one goes is decided in the cart sheet, where every line is visible —
    // the toast says so and opens it. A dish with only optional groups
    // holding ONLY a plain line is not ambiguous and decrements directly.
    // Same rule for a plain dish whose only line carries a note (SC-842):
    // it used to be a greyed-out « − » with no explanation.
    if (!plainLine || (hasOptions && hasOptionedLine)) {
      showToast(t('waiter.ticket.plats.optionedRemoveToast'), {
        label: t('waiter.ticket.plats.openCart'),
        onClick: onOpenCart,
      });
      return;
    }
    dispatch(setWaiterLineQty({ sessionUuid, lineId: plainLine.lineId, qty: plainLine.qty - 1 }));
  }

  return (
    <div hidden={hidden} data-testid="plats-view" className={`flex-1 min-h-0 flex-col ${hidden ? 'hidden' : 'flex'}`}>
      <div className="flex-1 min-h-0 flex">
        <nav
          data-testid="cat-rail"
          role="tablist"
          aria-orientation="vertical"
          // 112px under 380: at 360 the 130px rail left the product column
          // 202px, 4px short of « 7,80 € » + the stepper (44/44/56 then,
          // 56/44/56 since the − was widened) — so the `+` sat outside the
          // viewport and could not be tapped at all.
          className="w-[112px] min-[380px]:w-[130px] md:w-[220px] xl:w-[240px] shrink-0 border-e-2 border-hairline bg-paper overflow-y-auto waiter-scroll p-1 pt-2 pb-4 space-y-1"
        >
          {pickableCats.map((cat) => {
            const selected = cat.id === selectedCatId;
            const bg = categoryColor(cat);
            return (
              <button
                key={cat.id}
                type="button"
                data-testid={`cat-tile-${cat.id}`}
                role="tab"
                aria-selected={selected}
                onClick={() => setSelectedCatId(cat.id)}
                className={`w-full min-h-[44px] px-2 py-2 rounded-lg text-start font-sans text-[17px] leading-[1.15] ${
                  selected ? 'font-bold' : 'font-medium'
                }`}
                style={{
                  backgroundColor: bg,
                  color: onCategoryColor(bg),
                  boxShadow: selected ? 'inset 0 0 0 3px var(--ink)' : undefined,
                }}
              >
                <span className="line-clamp-2 block">{cat.name}</span>
              </button>
            );
          })}
        </nav>

        <div
          ref={productListRef}
          data-testid="product-list"
          // `auto-rows-max` (04-ux-mobile-spec.md §2.1) was never applied: the
          // implicit `auto` tracks all resolved to the rows' 88px min-height,
          // so a dish whose name takes two lines had its price + stepper line
          // clipped by the row's own `overflow-hidden` — the − / + were half
          // cut and half untappable on every phone width.
          className="flex-1 min-w-0 overflow-y-auto waiter-scroll p-1.5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 auto-rows-max gap-1.5 content-start"
        >
          {items.map((item) => {
            const groups = item.optionGroups ?? [];
            const hasOptions = groups.length > 0;
            const itemLines = linesByItem.get(item.id) ?? [];
            const qty = itemLines.reduce((s, l) => s + l.qty, 0);
            const inCart = qty > 0;
            const displayPrice = resolveModePrice(item, consumerMode);
            const showInclus = isBuffetDineIn && !!item.includedInBuffet;
            const fromCents = displayPrice === 0 && !showInclus ? fromPriceCents(item) : null;
            const photoUrl = item.photo ? thumbPhoto(item.photo) : '';
            // « − » is live whenever the dish is in the cart; handleMinus
            // decides between pulling a plain unit and the "open the cart"
            // toast (SC-842 — a greyed « − » next to a green row explained
            // nothing).
            const canStepMinus = qty > 0;
            // The old app's blue round button: shown as soon as the dish is
            // in the cart, opens note + options for the LAST plate added.
            // One control, one meaning — no ✎, no « Note › » hint, no
            // tappable name (SC-842).
            const showOptionsButton = qty > 0;
            // D11 — only the photo strip narrows the name column. The blue
            // button used to cap it too, and the cost was paid TWICE: the
            // button already takes its 44px+gap out of the flex row, and the
            // 60% was then re-applied to what was left. On a 391px phone that
            // left the name 107px (« Sushi Saumon… » clamped at two lines)
            // while the row's inline-end sat empty. The cap now lives on the
            // body column, so the percentage is of the ROW, and only a photo
            // triggers it.
            const capName = !!photoUrl;

            return (
              <div
                key={item.id}
                data-testid={`product-row-${item.id}`}
                data-qty={qty}
                data-has-options={hasOptions}
                data-in-cart={inCart}
                data-photo={!!photoUrl}
                className="relative overflow-hidden min-h-[88px] rounded-[10px] border p-2 flex flex-col justify-between"
                style={{
                  backgroundColor: inCart ? 'var(--w-incart-bg)' : undefined,
                  color: inCart ? 'var(--w-incart-fg)' : undefined,
                  borderColor: inCart ? 'var(--w-incart-fg)' : 'var(--hairline)',
                }}
              >
                {photoUrl && (
                  // D1 — the photo occupies the inline-end 62% of the row
                  // (was 45%: too narrow to recognise a dish at a glance),
                  // held at 75% opacity (was 55%, then 70%: the dishes read
                  // as ghosts on the handheld) and masked so it ramps up
                  // from its inline-start edge: the name/price column
                  // (capped at 72% via `capName`) overlaps the photo's first
                  // 55%, so that band is the one that must stay faint enough
                  // to read black text over.
                  //
                  // The mask starts at 0.4 (was 0.2, and before that at
                  // nothing: the inline-start edge kept reading as "too
                  // transparent" on the phone) with its 55% stop at 0.5.
                  // Effective alpha: left edge ~0.3, ~0.38 where the last
                  // characters of a long name land, then full 0.75 from 85%
                  // on: the half nobody writes over is solid.
                  <img
                    src={photoUrl}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    className="absolute inset-y-0 end-0 z-0 w-[62%] h-full object-cover"
                    style={{
                      opacity: 0.75,
                      WebkitMaskImage:
                        'linear-gradient(to right, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.5) 55%, #000 85%)',
                      maskImage:
                        'linear-gradient(to right, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.5) 55%, #000 85%)',
                    }}
                  />
                )}
                <div className="relative z-10 flex items-start gap-2">
                  {/* The name/price zone is INERT — like the old app, only
                      « + » adds and only « − » removes (SC-842). A tap here
                      used to open the sheet and add a plate: on a handheld
                      that is a plate the guest never ordered. */}
                  <div
                    data-testid={`product-row-body-${item.id}`}
                    className={`min-w-0 flex-1 text-start flex flex-col gap-0.5 ${capName ? 'max-w-[72%]' : ''}`}
                  >
                    <span className="font-sans text-[18px] leading-[1.2] line-clamp-3">
                      <DishCodeTag name={item.name} code={item.code} size="md" testId={`row-code-${item.id}`} />
                      {item.name}
                    </span>
                    <span data-testid={`row-price-${item.id}`} className="font-mono font-bold text-[18px] tabular-nums">
                      {showInclus
                        ? t('menu.includedInBuffet')
                        : fromCents !== null
                          ? t('menu.priceFrom', { price: formatPrice(fromCents) })
                          : formatPrice(displayPrice)}
                    </span>
                  </div>
                  {showOptionsButton && (
                    <button
                      type="button"
                      data-testid={`row-options-${item.id}`}
                      aria-label={
                        hasOptions
                          ? t('waiter.ticket.plats.optionsAria', { name: item.name })
                          : t('waiter.ticket.plats.noteAria', { name: item.name })
                      }
                      onClick={() => onEditLastLine(item.id)}
                      // `ms-auto`: the body column can now stop before the
                      // row's end (max-w), so the dot no longer rides along
                      // with it — it stays pinned to the inline end.
                      className="w-11 h-11 shrink-0 ms-auto flex items-center justify-center"
                    >
                      {/* The old app's blue dot, verbatim: an empty round
                          button, no glyph. 36px visual inside a 44px hit
                          box (the old one was 32px edge to edge). */}
                      <span
                        aria-hidden="true"
                        className="block w-9 h-9 rounded-full"
                        style={{ backgroundColor: 'var(--w-info)' }}
                      />
                    </button>
                  )}
                </div>
                {/* Wraps instead of overflowing: on a 320px phone the three
                    stepper controls alone can still overrun the row width. */}
                <div className="relative z-10 flex flex-wrap items-center justify-end mt-1 gap-1">
                  <div className="flex items-center gap-1.5">
                    {/* The « − » used to be a 1px-hairline ghost on a
                        transparent background next to a solid black « + » —
                        barely readable on paper, invisible over a photo, and
                        44px wide against the +'s 56. Same footprint as « + »,
                        opaque paper fill, 2px ink outline: an outlined twin
                        of the filled +, not a decoration. */}
                    <button
                      type="button"
                      data-testid={`row-minus-${item.id}`}
                      aria-label={t('menu.quickRemove', { name: item.name })}
                      disabled={!canStepMinus}
                      onClick={() => handleMinus(item, hasOptions)}
                      className={`w-14 h-11 border-2 border-ink rounded-lg bg-paper text-ink font-sans text-2xl font-bold leading-none ${photoUrl ? 'disabled:opacity-60' : 'disabled:opacity-40'}`}
                    >
                      −
                    </button>
                    <span
                      data-testid={`row-qty-${item.id}`}
                      className="w-11 h-11 flex items-center justify-center border-2 border-hairline rounded-lg bg-paper text-ink font-mono font-bold text-xl tabular-nums"
                    >
                      {qty}
                    </span>
                    <button
                      type="button"
                      data-testid={`row-plus-${item.id}`}
                      aria-label={t('menu.quickAdd', { name: item.name })}
                      onClick={() => handlePlus(item, groups, hasOptions)}
                      className="w-14 h-11 rounded-lg font-sans text-2xl font-bold leading-none text-white"
                      style={{ backgroundColor: 'var(--ink)' }}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div
        data-testid="plats-footer"
        className="shrink-0 grid grid-cols-[auto_1fr_auto] items-center gap-3 px-3 py-2 border-t-2 border-hairline bg-paper"
        style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          data-testid="cart-open"
          aria-label={t('waiter.ticket.plats.cartAria', { n: cartQty })}
          onClick={onOpenCart}
          className="relative w-[52px] h-[52px] rounded-full border-2 border-ink bg-paper text-ink flex items-center justify-center"
        >
          {/* The 🛒 emoji rendered as a different drawing on every device
              (and in colour on iOS, clashing with the surface). A masked
              house glyph inherits `currentColor` like the rest of the icon
              set. `key` restarts the roll animation on each add — remounting
              the span is what replays a CSS keyframe. */}
          <span
            key={cartBump}
            data-testid="cart-icon"
            className={`inline-flex ${cartBump > 0 ? 'w-cart-roll' : ''}`}
          >
            <MaskIcon src="/img/icon-cart.svg" className="w-7 h-7" />
          </span>
          {cartQty > 0 && (
            <span
              key={`badge-${cartBump}`}
              data-testid="cart-badge"
              className={`absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 rounded-full text-white text-[13px] font-bold flex items-center justify-center ${
                cartBump > 0 ? 'w-badge-pop' : ''
              }`}
              style={{ backgroundColor: 'var(--w-danger)' }}
            >
              {cartQty}
            </span>
          )}
        </button>
        <span
          data-testid="plats-total"
          className="text-center font-mono font-bold text-xl tabular-nums"
          aria-live="polite"
        >
          {formatPrice(totalCents)}
        </span>
        <button
          type="button"
          data-testid="plats-validate"
          disabled={cartQty === 0}
          onClick={onValidate}
          className="min-w-[120px] h-12 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40"
          style={{ backgroundColor: 'var(--w-success)' }}
        >
          {t('waiter.ticket.plats.validate')}
        </button>
      </div>
    </div>
  );
}
